/* eslint-disable @typescript-eslint/indent */
import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import * as moment from "moment";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { PaginatedResponse } from "../../../../core/http/paginated-response.type";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { DateQuestion } from "../../../../shared/components/dynamic-form/question-date";
import { DropdownOption, DropdownQuestion } from "../../../../shared/components/dynamic-form/question-dropdown";
import { LinkQuestion } from "../../../../shared/components/dynamic-form/question-link";
import { TextboxQuestion } from "../../../../shared/components/dynamic-form/question-textbox";
import { FORMAT } from "../../../../shared/constants/formats";
import { pattern } from "../../../../shared/constants/patterns";
import { setFilterCondition } from "../../../../shared/utils/helpers/common.helper";
import { isSameOrAfter, isSameOrBefore } from "../../../../shared/validators/validators";
import { CompanyService } from "../../companies/company.service";
import { Company } from "../../companies/company.type";
import { ContractService } from "../../contracts/contract.service";
import { MaintenanceProviderService } from "../../maintenance-provider/maintenance-provider.service";
import { MaintenanceProvider } from "../../maintenance-provider/maintenance-provider.types";
import { PartnerService } from "../../partners/partner.service";
import { Partner } from "../../partners/partner.type";
import { quoteTypes } from "../quote";
import { Quote } from "../quote.type";

@Injectable()
export class CreateQuoteService {
  constructor(
    private partnerService: PartnerService,
    private companyService: CompanyService,
    private maintenanceProviderService: MaintenanceProviderService,
    private contractService: ContractService,
  ) {}

  getMaintenanceProviders(query?: { name?: string; partnerId?: string }): Observable<DropdownOption[]> {
    return this.maintenanceProviderService
      .getMaintenanceProviders({
        where: [
          {
            isDefault: true,
            name: { $cont: query?.name },
          },
          {
            partners: { id: query?.partnerId },
            name: { $cont: query?.name },
          },
        ],
      })
      .pipe(
        map((data: PaginatedResponse<MaintenanceProvider>) =>
          data.items.map((c: MaintenanceProvider) => ({ value: c.id, label: c.name })),
        ),
      );
  }

  getPartner(partnerId: string): Observable<DropdownOption[]> {
    return this.partnerService.getPartner(partnerId).pipe(map((r: Partner) => [{ value: r.id, label: r.name }]));
  }

  getPartners(query?: { name: string }): Observable<DropdownOption[]> {
    return this.partnerService
      .getPartners({ where: setFilterCondition(query, "$cont") })
      .pipe(map((r: PaginatedResponse<Partner>) => r.items.map((c: Partner) => ({ value: c.id, label: c.name }))));
  }

  getCompanies(query: { partnerId: string; name?: string }): Observable<DropdownOption[]> {
    const { partnerId, ...filters } = query;
    return this.partnerService
      .getCompaniesByQuery(partnerId, { where: setFilterCondition(filters, "$cont") })
      .pipe(map((r: PaginatedResponse<Company>) => r.items.map((c: Company) => ({ value: c.id, label: c.name }))));
  }

  getAllCompanies(query?: { name?: string }): Observable<DropdownOption[]> {
    return this.companyService
      .getCompanies({ where: setFilterCondition(query, "$cont") })
      .pipe(
        map((data: PaginatedResponse<Company>) => data.items.map((c: Company) => ({ value: c.id, label: c.name }))),
      );
  }

  createDateQuestion(options?: { quote?: Quote; startDateSelected?: string; endDateSelected?: string }): DateQuestion {
    const value = options.quote?.startDate || options?.startDateSelected;
    const errors = [
      {
        key: "minDate",
        validator: isSameOrAfter(),
        message: "Date can`t be lower than 1/1/2000",
      },
    ];
    if (options.endDateSelected) {
      errors.push({
        key: "maxDate",
        validator: isSameOrBefore(options?.endDateSelected),
        message: `Start Date <strong>can't be greater than ${moment(options?.endDateSelected).format(
          FORMAT.DATE,
        )}</strong>`,
      });
    }
    const dateQuestion = new DateQuestion({
      value,
      key: "startDate",
      label: "Start Date",
      required: true,
      minDate: moment().add(1, "day").toDate(),
      errors,
      order: 3,
    });

    return dateQuestion;
  }

  generateQuestions(options: {
    quote?: Quote;
    partnerValue?: string;
    companyValue?: string;
    companyOptions?: DropdownOption[];
    companyEdit?: boolean;
    companySkip?: boolean;
    partnerSkip?: boolean;
    isManageView?: boolean;
    editMode?: boolean;
    showQuoteNumber?: boolean;
    maintenanceProviderValue?: MaintenanceProvider;
    quoteVersion?: number;
    partnerDisabled?: boolean;
  }): Observable<QuestionBase<string | boolean | number>[]> {
    if (options?.showQuoteNumber === null) {
      options.showQuoteNumber = false;
    }
    options = { companyEdit: true, editMode: false, ...options };
    const questions: QuestionBase<string | boolean | number>[] = [
      new TextboxQuestion({
        value: options.quote?.name,
        key: "name",
        label: "Name",
        required: true,
        order: 1,
        tooltipEnabled: true,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Name is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(2),
            message: "Name is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(200),
            message: "Name is <strong>too long</strong>",
          },
        ],
      }),
      options?.showQuoteNumber &&
        new TextboxQuestion({
          value: options?.quote?.number,
          key: "number",
          label: "Number",
          disabled: true,
          order: 2,
        }),
      this.createDateQuestion({
        quote: options?.quote,
        endDateSelected: options?.quote?.endDate,
      }),

      new DateQuestion({
        value: options.quote?.endDate,
        key: "endDate",
        label: "End Date",
        required: true,
        ...(!options.isManageView && {
          dependsOn: {
            key: "startDate",
          },
        }),
        errors: [
          {
            key: "minDate",
            validator: isSameOrAfter(),
            message: "Date can`t be lower than start date",
          },
          {
            key: "maxDate",
            validator: isSameOrAfter(),
            message: "Date scan`t be lower than start date",
          },
        ],
        order: 4,
      }),

      new DropdownQuestion({
        value: options.quote?.type,
        key: "type",
        label: "Type",
        options: quoteTypes,
        required: true,
        order: 5,
      }),

      new TextboxQuestion({
        value: options.quote?.sfOpportunity,
        key: "sfOpportunity",
        label: "SalesForce Opportunity",
        tooltipEnabled: true,
        errors: [
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "SalesForce Opportunity is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(600),
            message: "SalesForce Opportunity is <strong>too long</strong>",
          },
        ],
        order: 6,
      }),
      new DropdownQuestion({
        value: options.quote?.maintenanceProviderId,
        key: "maintenanceProviderId",
        label: "Maintenance Provider",
        required: false,
        tooltipEnabled: true,
        currentOption: {
          label: options.quote?.maintenanceProvider?.name,
          value: options.quote?.maintenanceProvider?.id,
        },
        asyncOptions: this.getMaintenanceProviders(),
        search: {
          filter: {
            callback: ({ value: name }) => {
              return this.getMaintenanceProviders({ name });
            },
          },
        },
        order: 7,
      }),

      !options.partnerSkip &&
        new DropdownQuestion({
          value: options.partnerValue,
          key: "partnerId",
          label: "Partner",
          required: true,
          tooltipEnabled: true,
          currentOption: {
            label: options.quote?.company?.partner?.name,
            value: options.quote?.company?.partner?.id,
          },
          options: options?.companyOptions,
          asyncOptions: options.partnerValue ? this.getPartner(options.partnerValue) : this.getPartners(),
          disabled: options.partnerDisabled,
          search: {
            filter: {
              callback: ({ value: name }) => this.getPartners({ name }),
            },
          },
          order: 8,
        }),

      !options?.companySkip &&
        new DropdownQuestion({
          value: options.companyValue || options.quote?.companyId,
          key: "companyId",
          label: "Company",
          tooltipEnabled: true,
          required: true,
          disabled: !options?.companyEdit,
          currentOption: {
            label: options.quote?.company?.name,
            value: options.quote?.company?.id,
          },
          options: options?.companyOptions,
          asyncOptions: options?.partnerValue
            ? this.getCompanies({
                partnerId: options?.partnerValue,
              })
            : undefined,
          dependsOn: {
            key: "partnerId",
            callback: ({ parentValue: partnerId }) => this.getCompanies({ partnerId }),
          },
          search: {
            filter: {
              parentKey: "partnerId",
              callback: ({ parentValue: partnerId, value: name }) =>
                this.getCompanies({
                  partnerId: options?.partnerValue || partnerId,
                  name,
                }),
            },
          },
          order: 9,
        }),

      options.editMode &&
        new DropdownQuestion({
          value: options.companyValue || options.quote?.companyId,
          key: "companyId",
          tooltipEnabled: true,
          label: "Company",
          required: false,
          disabled: true,
          currentOption: {
            label: options.quote?.company?.name,
            value: options.quote?.company?.id,
          },
          options: options?.companyOptions,
          asyncOptions: this.getAllCompanies(),
          search: {
            filter: {
              callback: ({ value: name }) => this.getAllCompanies({ name: name }),
            },
          },
          order: 10,
        }),

      options.quote?.contract &&
        new LinkQuestion({
          value: options.quote?.contract?.numberVersion,
          key: "contract",
          label: "Contract #",
          link: options.quote?.contract?.id ? `${this.contractService.path}/${options.quote?.contract?.id}` : null,
          order: 11,
        }),
    ];

    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }
}
